import { createApiClient, type ApiClient } from "@/lib/api/client";
import { createSessionStore, type SessionStore } from "@/lib/auth/session-store";
import { createSupabaseClient } from "@/lib/auth/supabase";
import { browserStorage, createDraftStore, type DraftStore } from "@/features/onboarding/draft-store";
import { createAnalytics, noopAnalytics, type Analytics } from "@/lib/analytics/analytics";
import { readConsent, waitsForConsent } from "@/lib/analytics/consent";
import { createGoogleSink } from "@/lib/analytics/google";
import { createPostHogSink } from "@/lib/analytics/posthog";
import type { Sink } from "@/lib/analytics/sink";
import { deviceTimeZone } from "@/lib/time-zone";
import { env, type Env } from "@/lib/env";

/**
 * The app's long-lived services, wired once.
 *
 * The API client reads the token through the session store on every call,
 * so a refresh Supabase performs in the background is picked up by the very
 * next request without anyone re-creating anything.
 */
export interface Services {
  readonly session: SessionStore;
  readonly api: ApiClient;
  /** The birth details being entered, persisted across the OAuth redirect. */
  readonly draft: DraftStore;
  /** Google Analytics and PostHog in a production build with keys set; otherwise nothing. */
  readonly analytics: Analytics;
}

/** Same-origin path the Worker forwards to PostHog (worker/index.ts). */
const POSTHOG_PROXY_PATH = "/ingest";

function createAnalyticsFor(config: Env): Analytics {
  // Local development never reports: it would count the team as visitors.
  if (!import.meta.env.PROD) return noopAnalytics;
  const storage = browserStorage();
  const consent = readConsent(storage);
  const sinks: Sink[] = [];
  if (config.gaMeasurementId) {
    sinks.push(createGoogleSink({ measurementId: config.gaMeasurementId, win: window, doc: document, consent }));
  }
  if (config.posthogKey) {
    sinks.push(
      createPostHogSink({
        token: config.posthogKey,
        apiHost: `${window.location.origin}${POSTHOG_PROXY_PATH}`,
        origin: window.location.origin,
        doc: document,
        load: () => import("@/lib/analytics/posthog-sdk").then((sdk) => sdk.default),
        consent,
        waitsForConsent: waitsForConsent(deviceTimeZone()),
      }),
    );
  }
  return sinks.length > 0 ? createAnalytics(sinks, storage) : noopAnalytics;
}

/** Binds analytics to whoever is signed in, and lets go on sign-out. */
function followSignIn(session: SessionStore, analytics: Analytics): void {
  let identified: string | null = null;
  const sync = () => {
    const { status, userId } = session.getSnapshot();
    if (status === "signed_in" && userId && userId !== identified) {
      identified = userId;
      analytics.identify(userId);
    } else if (status === "signed_out" && identified) {
      identified = null;
      analytics.forget();
    }
  };
  session.subscribe(sync);
  sync();
}

export function createServices(): Services {
  const supabase = createSupabaseClient(env);
  const session = createSessionStore(supabase.auth);
  const api = createApiClient({ baseUrl: env.apiBase, getToken: session.getToken });
  const draft = createDraftStore(browserStorage());
  const analytics = createAnalyticsFor(env);
  followSignIn(session, analytics);
  return Object.freeze({ session, api, draft, analytics });
}
